import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Search, Filter, RefreshCw, Smartphone, Apple, CheckCircle2, XCircle, ArrowUpDown, ArrowUp, ArrowDown, ChevronLeft, ChevronRight, Download, ExternalLink } from 'lucide-react';
import { appsService } from '../services/appsService';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
} from '@tanstack/react-table';

/**
 * Apps List Page
 * Displays all apps with filtering, sorting, and pagination
 */
const Apps = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [apps, setApps] = useState([]);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    per_page: 20,
    total_pages: 0,
  });
  const [filters, setFilters] = useState({
    platform: '',
    status: '',
    search: '',
  });
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('ASC');
  const [syncing, setSyncing] = useState(false);
  const [accountId, setAccountId] = useState(null);

  // Fetch apps
  const fetchApps = async (page = 1, sync = true) => {
    try {
      setLoading(true);
      setError(null);
      
      const params = {
        page,
        per_page: pagination.per_page,
        sort_by: sortBy,
        sort_order: sortOrder,
        sync: sync,
      };
      
      if (accountId) {
        params.account_id = accountId;
      }
      
      if (filters.platform) {
        params.platform = filters.platform;
      }
      
      if (filters.status) {
        params.status = filters.status;
      }
      
      if (filters.search) {
        params.search = filters.search;
      }
      
      const response = await appsService.getApps(params);
      
      setApps(response.data?.apps || []);
      setPagination(response.data?.pagination || pagination);
    } catch (err) {
      const errorMessage = err.message || err.toString() || 'Failed to load apps';
      setError(errorMessage);
      console.error('Apps fetch error:', err);
    } finally {
      setLoading(false);
      setSyncing(false);
    }
  };

  useEffect(() => {
    fetchApps(1, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortBy, sortOrder, filters.platform, filters.status, accountId]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (filters.search !== undefined) {
        fetchApps(1, false);
      }
    }, 500);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.search]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
    }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleSortChange = useCallback((field) => {
    if (sortBy === field) {
      setSortOrder(prev => prev === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(field);
      setSortOrder('ASC');
    }
  }, [sortBy]);

  const handleManualSync = async () => {
    setSyncing(true);
    await fetchApps(pagination.page, true);
  };

  const handlePageChange = (newPage) => {
    fetchApps(newPage, false);
  };

  // Table columns
  const columns = useMemo(() => [
    {
      accessorKey: 'app_name',
      header: () => (
        <div className="flex items-center gap-2 cursor-pointer" onClick={() => handleSortChange('name')}>
          App Name
          {sortBy === 'name' && (
            sortOrder === 'ASC' ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />
          )}
          {sortBy !== 'name' && <ArrowUpDown className="w-4 h-4 text-muted-foreground" />}
        </div>
      ),
      cell: ({ row }) => {
        const app = row.original;
        return (
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
              {app.platform === 'iOS' ? (
                <Apple className="w-6 h-6" />
              ) : (
                <Smartphone className="w-6 h-6" />
              )}
            </div>
            <div>
              <div className="font-medium">{app.app_name || app.app_key}</div>
              <div className="text-sm text-muted-foreground">{app.app_key}</div>
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: 'platform',
      header: 'Platform',
      cell: ({ getValue }) => {
        const platform = getValue();
        return (
          <Badge variant={platform === 'iOS' ? 'default' : 'secondary'}>
            {platform === 'iOS' ? (
              <><Apple className="w-3 h-3 mr-1" /> iOS</>
            ) : (
              <><Smartphone className="w-3 h-3 mr-1" /> Android</>
            )}
          </Badge>
        );
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => {
        const app = row.original;
        const status = app.status;
        const lastSync = app.last_sync;
        
        // Determine sync status indicator
        const getSyncStatusColor = () => {
          if (!lastSync) return 'text-muted-foreground';
          const syncDate = new Date(lastSync);
          const now = new Date();
          const diffMs = now - syncDate;
          const diffMins = Math.floor(diffMs / 60000);
          
          if (diffMins < 15) return 'text-green-600';
          if (diffMins < 60) return 'text-yellow-600';
          return 'text-red-600';
        };
        
        return (
          <div className="flex items-center gap-2">
            <Badge variant={status === 'active' ? 'default' : 'secondary'}>
              {status === 'active' ? (
                <><CheckCircle2 className="w-3 h-3 mr-1" /> Active</>
              ) : (
                <><XCircle className="w-3 h-3 mr-1" /> Inactive</>
              )}
            </Badge>
            {lastSync && (
              <div className={`w-2 h-2 rounded-full ${getSyncStatusColor()}`} title={`Last synced: ${new Date(lastSync).toLocaleString()}`} />
            )}
          </div>
        );
      },
    },
    {
      accessorKey: 'creation_date',
      header: () => (
        <div className="flex items-center gap-2 cursor-pointer" onClick={() => handleSortChange('creation_date')}>
          Created
          {sortBy === 'creation_date' && (
            sortOrder === 'ASC' ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />
          )}
          {sortBy !== 'creation_date' && <ArrowUpDown className="w-4 h-4 text-muted-foreground" />}
        </div>
      ),
      cell: ({ getValue }) => {
        const date = getValue();
        if (!date) return '-';
        return new Date(date).toLocaleDateString();
      },
    },
    {
      accessorKey: 'last_sync',
      header: () => (
        <div className="flex items-center gap-2 cursor-pointer" onClick={() => handleSortChange('last_sync')}>
          Last Sync
          {sortBy === 'last_sync' && (
            sortOrder === 'ASC' ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />
          )}
          {sortBy !== 'last_sync' && <ArrowUpDown className="w-4 h-4 text-muted-foreground" />}
        </div>
      ),
      cell: ({ getValue, row }) => {
        const date = getValue();
        const app = row.original;
        
        if (!date) {
          return (
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">Never</span>
              <div className="w-2 h-2 rounded-full bg-muted-foreground" title="No sync data" />
            </div>
          );
        }
        
        const syncDate = new Date(date);
        const now = new Date();
        const diffMs = now - syncDate;
        const diffMins = Math.floor(diffMs / 60000);
        
        // Determine freshness color
        let freshnessColor = 'text-green-600';
        let freshnessBg = 'bg-green-600';
        if (diffMins >= 60) {
          freshnessColor = 'text-yellow-600';
          freshnessBg = 'bg-yellow-600';
        }
        if (diffMins >= 1440) {
          freshnessColor = 'text-red-600';
          freshnessBg = 'bg-red-600';
        }
        
        let timeDisplay;
        if (diffMins < 60) {
          timeDisplay = `${diffMins}m ago`;
        } else if (diffMins < 1440) {
          timeDisplay = `${Math.floor(diffMins / 60)}h ago`;
        } else {
          timeDisplay = syncDate.toLocaleDateString();
        }
        
        return (
          <div className="flex items-center gap-2">
            <span className={diffMins >= 60 ? freshnessColor : ''}>{timeDisplay}</span>
            <div className={`w-2 h-2 rounded-full ${freshnessBg}`} title={`Last synced: ${syncDate.toLocaleString()}`} />
          </div>
        );
      },
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => {
        const app = row.original;
        return (
          <div className="flex items-center gap-2">
            <Button asChild variant="outline" size="sm">
              <Link to={`/apps/${app.app_key}`}>
                View Details
              </Link>
            </Button>
            <Button
              asChild
              variant="ghost"
              size="sm"
            >
              <Link to={`/analytics?appKey=${app.app_key}`}>
                <ExternalLink className="w-4 h-4" />
              </Link>
            </Button>
          </div>
        );
      },
    },
  ], [sortBy, sortOrder, handleSortChange]);

  const table = useReactTable({
    data: apps,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: true,
    pageCount: pagination.total_pages,
    state: {
      pagination: {
        pageIndex: pagination.page - 1,
        pageSize: pagination.per_page,
      },
    },
  });

  if (loading && apps.length === 0) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 overflow-x-hidden">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading apps...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 overflow-x-hidden">
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <p className="text-destructive mb-4">{error}</p>
            <Button onClick={() => fetchApps(pagination.page, false)} variant="destructive">
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 overflow-x-hidden">
      {/* Header */}
      <div className="mb-2 sm:mb-3 lg:mb-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2 gap-2">
          <div>
            <h1 className="text-lg sm:text-xl lg:text-2xl font-bold mb-0.5">Apps Management</h1>
            <p className="text-muted-foreground text-xs">
              Manage and view all your IronSource applications
            </p>
          </div>
          <div className="flex flex-wrap gap-1.5">
            <Button
              onClick={handleManualSync}
              disabled={syncing}
              variant="outline"
              size="sm"
              className="text-xs"
            >
              <RefreshCw className={`w-3 h-3 mr-1 ${syncing ? 'animate-spin' : ''}`} />
              {syncing ? 'Syncing...' : 'Sync Apps'}
            </Button>
            <Button
              asChild
              variant="outline"
              size="sm"
              className="text-xs"
            >
              <Link to="/analytics">
                <ExternalLink className="w-3 h-3 mr-1" />
                View Analytics
              </Link>
            </Button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <Card className="mb-2 sm:mb-3 lg:mb-4">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="w-5 h-5" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search by app name..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Platform</label>
              <Select
                value={filters.platform || undefined}
                onValueChange={(value) => handleFilterChange('platform', value === 'all' ? '' : value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Platforms" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Platforms</SelectItem>
                  <SelectItem value="iOS">iOS</SelectItem>
                  <SelectItem value="Android">Android</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Status</label>
              <Select
                value={filters.status || undefined}
                onValueChange={(value) => handleFilterChange('status', value === 'all' ? '' : value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              {(filters.platform || filters.status || filters.search) && (
                <Button
                  onClick={() => {
                    setFilters({ platform: '', status: '', search: '' });
                    setPagination(prev => ({ ...prev, page: 1 }));
                    // Force re-fetch with cleared filters
                    setTimeout(() => fetchApps(1, false), 0);
                  }}
                  variant="outline"
                  className="w-full"
                >
                  Clear Filters
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Apps Table */}
      <Card>
        <CardHeader>
          <CardTitle>
            Apps ({pagination.total})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-border">
              <thead className="bg-muted/50">
                {table.getHeaderGroups().map(headerGroup => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map(header => (
                      <th
                        key={header.id}
                        className="px-2 sm:px-4 lg:px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody className="divide-y divide-border">
                {table.getRowModel().rows.map(row => (
                  <tr key={row.id} className="hover:bg-muted/50">
                    {row.getVisibleCells().map(cell => (
                      <td key={cell.id} className="px-2 sm:px-4 lg:px-6 py-4 whitespace-nowrap text-sm">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="mt-4 flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              Showing {((pagination.page - 1) * pagination.per_page) + 1} to{' '}
              {Math.min(pagination.page * pagination.per_page, pagination.total)} of{' '}
              {pagination.total} apps
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1}
                variant="outline"
                size="sm"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Previous
              </Button>
              <span className="text-sm text-muted-foreground">
                Page {pagination.page} of {pagination.total_pages}
              </span>
              <Button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page >= pagination.total_pages}
                variant="outline"
                size="sm"
              >
                Next
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Apps;
